import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Avatar, Button, Card, Tag, Tooltip, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  UpOutlined,
  UserOutlined,
} from "@ant-design/icons";
import FieldInput from "./FieldInput";
import ProfessionalPicker, { roleLabel } from "./ProfessionalPicker";
import {
  addItem,
  canAddItem,
  canHideItem,
  displayItems,
  invalidItemIds,
  isRemovable,
  itemSummary,
  listEqualsFallback,
  moveItem,
  newItemId,
  removeItem,
  setItemPart,
  toggleHidden,
} from "./listValue";
import { fetchProfessionalsByIds } from "./pagesApi";

const { Text } = Typography;

export const STRUCTURE_NOTICE = "Подредбата и скриването се управляват от българската версия";
export const INVALID_IDS_NOTICE = "Невалидни идентификатори — ще бъдат премахнати при запис";

const hintFor = (field) => {
  if (field.picker === "professional") {
    return "Стрелките сменят реда, окото скрива професионалист от сайта.";
  }
  return field.addable
    ? "Стрелките сменят реда, окото скрива елемент, „Добави“ създава нов."
    : "Стрелките сменят реда, окото скрива елемент. Нови елементи се добавят от екипа.";
};

/**
 * One `list` field (DAB-852): an ordered set of item cards.
 *
 * Every card shows what the item *is* in one line — its title, or the
 * professional's name — with the actions beside it: up/down arrows (drag-free,
 * so it works with a thumb on a phone), an eye to hide it from the site, and a
 * delete that only items created here can use. Tapping the title opens the
 * item's parts, each an ordinary `FieldInput`, so "От сайта" / "Променено"
 * behave exactly as they do for every other field.
 *
 * On Bulgarian the editor owns the structure. On any other language the order
 * and hidden flags come from the Bulgarian draft (`structure`) and only the
 * text is editable — the notice says so, rather than leaving arrows that do
 * nothing.
 *
 * All list logic is in `listValue.js`; this file only renders it.
 */
function ListInput({ field, value, onChange, lang, structure, onAssist, focus, onFocused }) {
  const isStructure = lang === "bg";
  const isPicker = field.picker === "professional";
  const [expanded, setExpanded] = useState(() => new Set());
  // Picker lists: id -> card data, `null` once the API has said "not found".
  const [people, setPeople] = useState({});

  // Handlers read the latest props through refs, so each (item, part) handler
  // can be created once and `FieldInput`'s memoization stays effective.
  const latest = useRef({ field, value, lang });
  latest.current = { field, value, lang };

  const rows = useMemo(
    () => displayItems(field, value, lang, structure),
    [field, value, lang, structure],
  );
  const isOverridden = !listEqualsFallback(field, value, lang);
  const invalidIds = isStructure ? invalidItemIds(field, value) : [];
  const canAdd = isStructure && canAddItem(field, value);

  const update = (next) => {
    if (next !== latest.current.value) onChange(next);
  };

  const idsKey = isPicker ? rows.map((row) => row.id).join(",") : "";
  useEffect(() => {
    if (!isPicker || !idsKey) return undefined;
    const missing = idsKey.split(",").filter((id) => !(id in people));
    if (!missing.length) return undefined;
    // An id that is not a professional id cannot be looked up; it is flagged
    // "not found" straight away and dropped on save (`invalidItemIds`).
    const invalid = invalidItemIds(field, { items: missing.map((id) => ({ id })) });
    const lookup = missing.filter((id) => !invalid.includes(id));
    if (!lookup.length) {
      setPeople((current) => ({ ...current, ...Object.fromEntries(invalid.map((id) => [id, null])) }));
      return undefined;
    }
    let cancelled = false;
    fetchProfessionalsByIds(lookup)
      .then((found) => {
        if (cancelled) return;
        const byId = Object.fromEntries((found || []).map((row) => [row.id, row]));
        setPeople((current) => ({
          ...current,
          ...Object.fromEntries(missing.map((id) => [id, byId[id] || null])),
        }));
      })
      .catch(() => {
        // The ids stay listed as ids; the list is still fully editable.
      });
    return () => {
      cancelled = true;
    };
    // `people` is deliberately not a dependency: it is what this effect fills.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicker, idsKey]);

  // Click-to-edit (DAB-902): open the item the preview named; its part then
  // focuses itself through `focusNonce`.
  const focusItemId = focus?.itemId;
  const focusNonce = focus?.nonce;
  useEffect(() => {
    if (!focusItemId) return;
    setExpanded((current) => (current.has(focusItemId) ? current : new Set(current).add(focusItemId)));
  }, [focusItemId, focusNonce]);

  const handlers = useRef(new Map());
  const partHandler = (id, key) => {
    const handlerKey = `${id} ${key}`;
    if (!handlers.current.has(handlerKey)) {
      handlers.current.set(handlerKey, (part) => {
        const current = latest.current;
        update(setItemPart(current.field, current.value, id, key, part, current.lang));
      });
    }
    return handlers.current.get(handlerKey);
  };

  const toggleExpanded = (id) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const add = () => {
    const id = newItemId();
    update(addItem(field, value, id));
    // A new item has nothing to show until it is filled in — open it.
    setExpanded((current) => new Set(current).add(id));
  };

  const addProfessional = (row) => {
    setPeople((current) => ({ ...current, [row.id]: row }));
    update(addItem(field, value, row.id));
  };

  const reset = () => {
    setExpanded(new Set());
    onChange(null);
  };

  const partField = (row, part) => ({
    path: `${field.path}.${row.id}.${part.key}`,
    kind: part.kind,
    label: part.label,
    fallback: row.committed ? row.committed[part.key] : undefined,
    listSource: { path: field.path, id: row.id, key: part.key },
  });

  return (
    <div className="mp-list">
      <div className="mp-list__header">
        <Text strong>{field.label}</Text>
        {isOverridden ? <Tag color="orange">Променено</Tag> : <Tag>От сайта</Tag>}
        {isOverridden && (
          <Button type="link" size="small" className="mp-link-action" onClick={reset}>
            Върни както е на сайта
          </Button>
        )}
      </div>

      {isStructure ? (
        <Text type="secondary" className="mp-list__hint">
          {hintFor(field)}
        </Text>
      ) : (
        <Alert type="info" showIcon className="mp-list__notice" message={STRUCTURE_NOTICE} />
      )}

      {invalidIds.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className="mp-list__notice"
          message={`${INVALID_IDS_NOTICE}: ${invalidIds.join(", ")}`}
        />
      )}

      {rows.map((row, index) => {
        const person = isPicker ? people[row.id] : undefined;
        const isOpen = !isPicker && expanded.has(row.id);
        const hideAllowed = canHideItem(field, value, row.id);
        const removable = isRemovable(field, row.id);
        const parts = field.itemFields || [];

        return (
          <Card
            key={row.id}
            size="small"
            className={`mp-list-item${row.hidden ? " mp-list-item--hidden" : ""}`}
          >
            <div className="mp-list-item__row">
              <div className="mp-list-item__main">
                <span className="mp-list-item__index">{index + 1}.</span>
                {isPicker ? (
                  <>
                    <Avatar src={person?.photo || undefined} icon={<UserOutlined />} />
                    <div className="mp-list-item__person">
                      <span className="mp-list-item__summary">{person?.name || row.id}</span>
                      {person?.role && (
                        <Text type="secondary" className="mp-list-item__meta">
                          {roleLabel(person.role)}
                        </Text>
                      )}
                    </div>
                  </>
                ) : (
                  <Button
                    type="text"
                    className="mp-list-item__summary"
                    aria-expanded={isOpen}
                    onClick={() => toggleExpanded(row.id)}
                  >
                    {itemSummary(field, row)}
                  </Button>
                )}
                <div className="mp-list-item__tags">
                  {row.hidden && <Tag>Скрито</Tag>}
                  {row.isNew && !isPicker && <Tag color="blue">Ново</Tag>}
                  {row.missing.length > 0 &&
                    (isStructure ? (
                      <Tag color="red">Непълно</Tag>
                    ) : (
                      <Tag color="gold">Липсва превод</Tag>
                    ))}
                  {isPicker && person === null && <Tag color="red">Не е намерен</Tag>}
                  {isPicker && person && !person.visible && (
                    <Tag color="gold">Не се показва в сайта</Tag>
                  )}
                </div>
              </div>

              <div className="mp-list-item__actions">
                {isStructure && (
                  <>
                    <Button
                      className="mp-list-item__action"
                      icon={<ArrowUpOutlined />}
                      aria-label="Премести нагоре"
                      disabled={index === 0}
                      onClick={() => update(moveItem(field, value, row.id, "up"))}
                    />
                    <Button
                      className="mp-list-item__action"
                      icon={<ArrowDownOutlined />}
                      aria-label="Премести надолу"
                      disabled={index === rows.length - 1}
                      onClick={() => update(moveItem(field, value, row.id, "down"))}
                    />
                    <Tooltip
                      title={hideAllowed ? undefined : "Поне един елемент трябва да остане видим"}
                    >
                      <Button
                        className="mp-list-item__action"
                        icon={row.hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        aria-label={row.hidden ? "Покажи" : "Скрий"}
                        aria-pressed={row.hidden}
                        disabled={!hideAllowed}
                        onClick={() => update(toggleHidden(field, value, row.id))}
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        removable
                          ? undefined
                          : "Елементите от сайта не се изтриват — скрий ги с окото"
                      }
                    >
                      <Button
                        className="mp-list-item__action"
                        danger={removable}
                        icon={<DeleteOutlined />}
                        aria-label="Изтрий"
                        disabled={!removable}
                        onClick={() => update(removeItem(field, value, row.id))}
                      />
                    </Tooltip>
                  </>
                )}
                {!isPicker && (
                  <Button
                    type="text"
                    className="mp-list-item__action"
                    icon={isOpen ? <UpOutlined /> : <DownOutlined />}
                    aria-label={isOpen ? "Затвори" : "Отвори"}
                    onClick={() => toggleExpanded(row.id)}
                  />
                )}
              </div>
            </div>

            {isOpen && (
              <div className="mp-list-item__body">
                {parts.map((part) =>
                  part.locked ? (
                    <div key={part.key} className="mp-list-item__locked">
                      <Text strong>{part.label}</Text> <Tag>Зададено в кода</Tag>{" "}
                      <Text type="secondary">{String(row.committed?.[part.key] ?? "")}</Text>
                    </div>
                  ) : (
                    <FieldInput
                      key={`${lang}-${part.key}`}
                      field={partField(row, part)}
                      value={row.parts[part.key]}
                      onChange={partHandler(row.id, part.key)}
                      onAssist={onAssist}
                      startOpen={row.isNew}
                      focusNonce={
                        focus?.itemId === row.id && focus.key === part.key ? focus.nonce : undefined
                      }
                      onFocused={onFocused}
                    />
                  ),
                )}
              </div>
            )}
          </Card>
        );
      })}

      {isStructure &&
        field.addable &&
        (isPicker ? (
          <ProfessionalPicker
            onSelect={addProfessional}
            excludeIds={rows.map((row) => row.id)}
            disabled={!canAdd}
          />
        ) : (
          <Button
            icon={<PlusOutlined />}
            block
            onClick={add}
            disabled={!canAdd}
            className="mp-list__add"
          >
            Добави
          </Button>
        ))}
      {isStructure && field.addable && !canAdd && (
        <Text type="secondary" className="mp-list__hint">
          Списъкът е пълен — най-много {field.max} елемента.
        </Text>
      )}
    </div>
  );
}

export default React.memo(ListInput);
